import apiClient from './apiClient';
import type {
  CreateWorkOrderRequest,
  WorkOrder,
  WorkOrderDeletePreview,
  WorkOrderDeleteResult,
  WorkOrderDetail,
  WorkOrderDetailWithRelations,
} from '../types/workOrder';

export const getWorkOrdersByProject = async (
  projectKey: string,
): Promise<WorkOrder[]> => {
  const response = await apiClient.get<WorkOrder[]>(
    `/workorders/project/${projectKey}`,
  );
  return response.data;
};

export const getWorkOrderById = async (
  id: string,
): Promise<{ workOrder: WorkOrder; details: WorkOrderDetailWithRelations[] }> => {
  const response = await apiClient.get<{
    workOrder: WorkOrder;
    details: WorkOrderDetailWithRelations[];
  }>(`/workorders/${id}`);
  return response.data;
};

export const createWorkOrder = async (
  projectKey: string,
  data: CreateWorkOrderRequest,
): Promise<WorkOrder> => {
  const response = await apiClient.post<WorkOrder>(
    `/workorders/project/${projectKey}`,
    data,
  );
  return response.data;
};

export const updateWorkOrderDetail = async (
  id: string,
  data: Record<string, unknown>,
  opts: { confirmReset?: boolean } = {},
): Promise<WorkOrderDetail> => {
  const response = await apiClient.put<WorkOrderDetail>(
    `/workorders/details/${id}`,
    { ...data, ...(opts.confirmReset ? { confirmReset: true } : {}) },
  );
  return response.data;
};

/** One old→new value pair, rendered as chips in the history drawer. */
export interface WoTimelineChange {
  label: string;
  from: string | null;
  to: string | null;
}

/** One entry in the merged audit timeline (edits + signoff resets + reports). */
export interface WoTimelineEvent {
  key: string;
  type: string;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  title: string;
  changes: WoTimelineChange[];
  summary: string;
  userEmail: string;
  userName: string | null;
  timestamp: string;
  workOrderDetailKey: string | null;
  sampleId: string | null;
  specimenRef: string | null;
  testName: string | null;
}

export const getWorkOrderTimeline = async (workOrderKey: string): Promise<WoTimelineEvent[]> => {
  const response = await apiClient.get<WoTimelineEvent[]>(`/workorders/${workOrderKey}/events`);
  return response.data;
};

export const getDetailTimeline = async (workOrderDetailKey: string): Promise<WoTimelineEvent[]> => {
  const response = await apiClient.get<WoTimelineEvent[]>(`/workorders/details/${workOrderDetailKey}/events`);
  return response.data;
};

export const bulkUpdateDetailField = async (
  keys: string[],
  field: string,
  value: string | null,
): Promise<{ updated: number }> => {
  const response = await apiClient.put<{ updated: number }>(
    '/workorders/details/bulk-update-field',
    { keys, field, value },
  );
  return response.data;
};

export interface ProjectTestRow {
  WorkOrderDetailKey: string;
  SpecimenRef: string | null;
  TestStatus: string;
  CheckedBy: string | null;
  TestDate: string | null;
  SampleId: string;
  LocationId: string;
  LocationName: string;
  TestName: string;
  /** Stable dispatch slug (BUG-2026-0197); null when the test has no code.
   *  Identity checks (e.g. work-order-batch reports) key on this, not TestName. */
  TestCode: string | null;
  WorkOrderId: string;
  WorkOrderKey: string;
}

export const listProjectTests = async (projectKey: string): Promise<ProjectTestRow[]> => {
  const response = await apiClient.get<ProjectTestRow[]>(`/workorders/project/${projectKey}/tests`);
  return response.data;
};

export const generateBulkReports = async (workOrderDetailKeys: string[]): Promise<Blob> => {
  const response = await apiClient.post('/workorderdetails/bulk-report',
    { workOrderDetailKeys }, { responseType: 'blob' });
  return response.data as Blob;
};

/**
 * P2-4: schedule side-effects returned by a WO status transition.
 * Hand-mirrored from backend workOrderLifecycleService.WorkOrderUpdateResult.
 */
export interface WorkOrderUpdateResponse extends WorkOrder {
  /** Set on a transition to 'Cancelled' — the released schedule counts. */
  scheduleRelease?: {
    tasksReleased: number;
    bookingsReleased: number;
    assignmentsReleased?: number;
  };
  /** Draft plans auto-deleted on the 'Cancelled' transition. */
  scenariosDeleted?: number;
  /** Set on a transition to 'Completed' when future days were trimmed. */
  scheduleTrim?: {
    bookingsReleased: number;
    bookingsTrimmed: number;
    assignmentsReleased: number;
  };
}

export const updateWorkOrder = async (
  id: string,
  data: Partial<Pick<WorkOrder, 'WorkOrderStatus' | 'WorkOrderName' | 'Remarks' | 'LabLocation'>>,
  opts?: { releaseSchedule?: boolean },
): Promise<WorkOrderUpdateResponse> => {
  // P2-4: releaseSchedule=true is sent only after the user confirmed releasing
  // committed work on a transition to 'Cancelled' (409 SOURCE_RELEASES_COMMITTED).
  const url = opts?.releaseSchedule
    ? `/workorders/${id}?releaseSchedule=true`
    : `/workorders/${id}`;
  const response = await apiClient.put<WorkOrderUpdateResponse>(url, data);
  return response.data;
};

export const addSamplesToWorkOrder = async (
  workOrderId: string,
  sampleKeys: string[],
  testKeys: string[],
  specimenCounts?: Record<string, number>,
): Promise<void> => {
  await apiClient.post(`/workorders/${workOrderId}/samples`, { sampleKeys, testKeys, specimenCounts });
};

export const bulkImportDetails = async (
  workOrderId: string,
  rows: { SampleReference: string; TestName: string; Priority?: string; Instructions?: string; SpecimenRef?: string }[],
): Promise<{ created: number; errors: string[] }> => {
  const response = await apiClient.post<{ created: number; errors: string[] }>(
    `/workorders/${workOrderId}/bulk-import`,
    { rows },
  );
  return response.data;
};

/** Read-only inventory of what deleting the work order will touch (feeds the confirm dialog). */
export const getWorkOrderDeletePreview = async (id: string): Promise<WorkOrderDeletePreview> => {
  const response = await apiClient.get<WorkOrderDeletePreview>(`/workorders/${id}/delete-preview`);
  return response.data;
};

export const deleteWorkOrder = async (
  id: string,
  opts?: { releaseSchedule?: boolean },
): Promise<WorkOrderDeleteResult> => {
  // releaseSchedule=true is sent only when the user has explicitly confirmed
  // releasing a finalized (live/committed) schedule attached to this work order.
  const url = opts?.releaseSchedule
    ? `/workorders/${id}?releaseSchedule=true`
    : `/workorders/${id}`;
  const response = await apiClient.delete<WorkOrderDeleteResult>(url);
  return response.data;
};

/** G2-2: counts of schedule days freed by an execution-feedback trim. */
export interface WodRemovalTrim {
  bookingsReleased: number;
  bookingsTrimmed: number;
  assignmentsReleased: number;
}

/**
 * G2-4: returns { detailsRemoved, scheduledTasks } — scheduledTasks > 0 means
 * the removed tests still have active ScheduleTasks and the schedule is now
 * out of sync until the user runs "Sync now".
 * G2-2: scheduleTrim reports the tasks' FUTURE bookings/staffing released by
 * the removal (a cancelled test stops occupying machines and people).
 */
export const removeWorkOrderDetails = async (
  keys: string[],
): Promise<{ detailsRemoved: number; scheduledTasks: number; scheduleTrim?: WodRemovalTrim }> => {
  const response = await apiClient.post<{
    detailsRemoved: number; scheduledTasks: number; scheduleTrim?: WodRemovalTrim;
  }>(
    '/workorders/details/bulk-delete',
    { keys },
  );
  return response.data ?? { detailsRemoved: keys.length, scheduledTasks: 0 };
};

/** Fetch active team member display names for dropdowns.
 *  Optional signoff filter: 'check' → users who may be named Tested By.
 *  RBAC overhaul Phase C: pass a WorkOrderDetail key to scope 'review' /
 *  'approve' to that row's test skill — 'review' returns level-4+ holders plus
 *  admins; 'approve' returns the level-9 approver designates. Without wodKey,
 *  'approve' falls back to the legacy role-based list. */
export const getTeamMembers = async (
  signoff?: 'check' | 'review' | 'approve',
  wodKey?: string,
): Promise<string[]> => {
  const response = await apiClient.get<string[]>('/users/team', {
    params: signoff ? { signoff, ...(wodKey ? { wod: wodKey } : {}) } : undefined,
  });
  return response.data;
};

/** The caller's own skill levels (Phase C — per-row review gating). */
export interface MySkillLevel {
  SkillKey: string;
  SkillName: string;
  Level: number;
}

/** One signoff-dropdown candidate: name + their level in the ROW's skill. */
export interface SignoffOption {
  name: string;
  level: number | null;
}

export interface WodSignoffOptions {
  checkers: SignoffOption[];
  reviewers: SignoffOption[];
  designates: SignoffOption[];
}

/** All three signoff dropdowns for a WOD in one call, levels included —
 *  rendered with skill chips like the scenario-planning assignee picker. */
export const getSignoffOptionsForWod = async (wodKey: string): Promise<WodSignoffOptions> => {
  const response = await apiClient.get<WodSignoffOptions>('/users/team', {
    params: { signoff: 'rich', wod: wodKey },
  });
  return response.data;
};

export const getMySkillLevels = async (): Promise<MySkillLevel[]> => {
  const response = await apiClient.get<MySkillLevel[]>('/users/me/skills');
  return response.data;
};
